/**
 * SOLID Review
 * S — Strong. One concern: what the session intends once identity and the stored game have settled — and the step each
 *     intent takes.
 * O — Strong. A new intent is one step in the route; the route's type refuses an intent left out.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. One operation.
 * D — Strong. The session's deps and the route's steps, by role.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { ICommandPlanIntent, IEntityIntent } from '../../domain/ports/schemas/SessionMoves';
import type { IConfigDepSession } from './IConfigDepSession';

/** One way on from an intent: answers whether the intent is asked again — with the cold check then done. */
export interface IServiceEntryStep {
  take(ctx: IContextRequest): Promise<boolean>;
}

/** A step for every intent the session can have — or tsc refuses the route. */
export type TEntryRoute = { readonly [N in IEntityIntent['next']]: IServiceEntryStep };

/** Seated, idle (the decision sent the session back to the welcome screen) or waiting for a name: nothing left to do. */
export class EntryEnds implements IServiceEntryStep {
  async take(_ctx: IContextRequest): Promise<boolean> {
    return false;
  }
}

/**
 * THE SESSION'S ENTRY ROUTE (FE2's `proceed`; a map since frontend-application-oo P3, replacing its `switch` on the intent).
 * The session's decision says what it intends — a pending code to join, a registered player's cold rejoin, the host intent,
 * or nothing more — and the route takes that intent's step. A join that SETTLED and a cold rejoin ask the intent again, the
 * cold check done; the host's create ends the entry.
 */
export class SessionEntryRoute {
  constructor(private readonly deps: IConfigDepSession, private readonly route: TEntryRoute) {}

  async proceed(ctx: IContextRequest, coldChecked: boolean): Promise<void> {
    const planned: ICommandPlanIntent = { coldChecked };
    const intent = await this.deps.step.run(ctx, (input) => this.deps.decisions.planIntent(ctx, input, planned));
    if (!intent.success) return;
    if (await this.route[intent.value.next].take(ctx)) return this.proceed(ctx, true);
  }
}
